import { get, post, put, getBlob, postBlob, del } from '../../api/api.service';
import { buildContractEmailBody, buildContractEmailSubject } from './customer-contract-email.templates';

export const CONTRACT_STATUS_DRAFT = 0;
export const CONTRACT_STATUS_PENDING_SIGNATURE = 1;
export const CONTRACT_STATUS_ACTIVE = 2;
export const CONTRACT_STATUS_EXPIRED = 3;
export const CONTRACT_STATUS_CANCELLED = 4;

export interface CustomerContractListItem {
  id: number;
  contractNumber: string;
  signUpUserId: number;
  isDraft: boolean;
  contractStatus: number;
  isArchived: boolean;
  customer: {
    customerId: number;
    name: string;
    email: string;
    orgNo: string;
  };
  startDate: string;
  endDate: string;
  noEndDate: boolean;
  amount: number;
  currency: string;
  compensationType: string;
  createdDate: string;
  sentDate?: string | null;
}

export interface GetAllCustomerContractsResponse {
  statusCode: number;
  message: string;
  response: CustomerContractListItem[];
  totalCount: number;
}

export const getAllCustomerContractsApi = async (): Promise<GetAllCustomerContractsResponse> => {
  return get('/customercontractv2/get-all');
};

export interface PreferredContractLanguageResponse {
  statusCode: number;
  message: string;
  response: string;
  totalCount: number;
  numberOfPages: number;
}

// `locale` is the fallback to use if the freelancer has no saved preference
// yet; `response` is the actual preferred contract language.
export const getPreferredContractLanguageApi = async (
  locale: string
): Promise<PreferredContractLanguageResponse> => {
  return get('/customercontractv2/preferred-language', { locale });
};

export const getArchivedCustomerContractsApi = async (): Promise<GetAllCustomerContractsResponse> => {
  return get('/customercontractv2/get-archived');
};

export interface CreateCustomerContractPayload {
  id?: number;
  customerId: number;
  offeredServices: string;
  assignmentPeriod: string;
  workDescription: string;
  specialTerms: string;
  startDate: string;
  endDate: string;
  noEndDate: boolean;
  hasNoticePeriod: boolean;
  noticePeriodDuration: string;
  assignmentDescription: string;
  compensationType: string;
  numberOfUnits: number;
  currency: string;
  amount: number;
  isTermsAccepted: boolean;
  freelancerSignatureDate: string;
  freelancerSignatureData: string;
  freelancerNameClarification: string;
  // Persisted server-side as the freelancer's preferred contract language —
  // GET /customercontractv2/preferred-language later returns whatever was
  // sent here on the most recent create/update call.
  locale: string;
}

export interface CreateCustomerContractResponse {
  statusCode: number;
  message: string;
}

export const createCustomerContractApi = async (
  payload: CreateCustomerContractPayload
): Promise<CreateCustomerContractResponse> => {
  const response = await post<CreateCustomerContractResponse>(
    '/customercontractv2/create',
    payload
  );
  return response;
};

// Renders a PDF from unsaved form data — nothing is persisted. Used to show
// the sender a preview before they've saved/sent anything.
export interface PreviewContractPdfPayload {
  id?: number;
  customerId: number;
  language: string;
  offeredServices: string;
  assignmentPeriod: string;
  workDescription: string;
  specialTerms: string;
  startDate: string;
  endDate: string;
  noEndDate: boolean;
  hasNoticePeriod: boolean;
  noticePeriodDuration: string;
  assignmentDescription: string;
  compensationType: string;
  currency: string;
  amount: number;
  numberOfUnits: number;
  isTermsAccepted: boolean;
  customerSignatureDate: string;
  customerSignatureData: string;
  customerNameClarification: string;
  freelancerSignatureDate: string;
  freelancerSignatureData: string;
  freelancerNameClarification: string;
  isDraft: boolean;
}

export const previewContractPdfApi = async (
  payload: PreviewContractPdfPayload,
  locale: string
): Promise<Blob> => {
  return postBlob('/customercontractv2/preview-pdf', payload, { locale });
};

export interface CustomerContractDetail {
  id: number;
  contractNumber: string;
  signUpUserId: number;
  customer: {
    customerId: number;
    name: string;
    email: string;
    orgNo: string;
    address?: string;
    city?: string;
    zipCode?: string;
  };
  offeredServices: string;
  assignmentPeriod: string;
  workDescription: string;
  specialTerms: string;
  startDate: string;
  endDate: string;
  noEndDate: boolean;
  hasNoticePeriod: boolean;
  noticePeriodDuration: string;
  assignmentDescription: string;
  compensationType: string;
  numberOfUnits: number;
  currency: string;
  amount: number;
  isTermsAccepted: boolean;
  freelancerSignatureDate: string;
  freelancerSignatureData: string;
  freelancerNameClarification: string;
  contractStatus: number;
  contractStatusLabel: string;
}

export interface GetCustomerContractByIdResponse {
  statusCode: number;
  message: string;
  response: CustomerContractDetail;
}

export const getCustomerContractByIdApi = async (id: number): Promise<GetCustomerContractByIdResponse> => {
  return get(`/customercontractv2/${id}`);
};

export const downloadContractPdfApi = async (id: number, locale: string): Promise<Blob> => {
  return getBlob(`/customercontractv2/${id}/download-pdf`, { locale });
};

export interface SendContractEmailPayload {
  toEmails: string[];
  subject: string;
  body: string;
  attachPdf: boolean;
  locale: string;
}

export interface SendContractEmailResponse {
  statusCode: number;
  message: string;
}

export const sendContractEmailApi = async (
  id: number,
  payload: SendContractEmailPayload
): Promise<SendContractEmailResponse> => {
  return post(`/customercontractv2/${id}/send-email`, payload);
};

// Shared by the contracts grid's "send" action and the create-contract
// wizard's "Send Agreement for Signature" button, so both build the exact
// same email subject/body and hit the exact same endpoint.
export interface ContractEmailSummary {
  customerName: string;
  customerEmail: string;
  contractNumber: string;
  startDate: string;
  endDate: string | null;
  noEndDate: boolean;
  amount: number;
  currency: string;
  compensationType: string;
}

// Draft / Pending Signature contracts haven't been agreed to yet, so they go
// out with an empty subject/body and let the backend generate its own
// (sign-invitation) content. Only Active contracts get the client-built
// subject + HTML template — body is otherwise ignored by the backend anyway.
export const sendContractAgreementEmail = async (
  id: number,
  summary: ContractEmailSummary,
  senderName: string,
  locale: string,
  contractStatus: number
): Promise<SendContractEmailResponse> => {
  if (contractStatus !== CONTRACT_STATUS_ACTIVE) {
    return sendContractEmailApi(id, {
      toEmails: [summary.customerEmail],
      subject: '',
      body: '',
      attachPdf: true,
      locale,
    });
  }

  const emailData = {
    customerName: summary.customerName,
    senderName,
    contractNumber: summary.contractNumber,
    startDate: summary.startDate,
    endDate: summary.endDate,
    noEndDate: summary.noEndDate,
    amount: summary.amount,
    currency: summary.currency,
    compensationType: summary.compensationType,
    locale,
  };

  return sendContractEmailApi(id, {
    toEmails: [summary.customerEmail],
    subject: buildContractEmailSubject(emailData),
    body: buildContractEmailBody(emailData),
    attachPdf: true,
    locale,
  });
};

export interface DeleteCustomerContractResponse {
  statusCode: number;
  message: string;
}

export const deleteCustomerContractApi = async (id: number): Promise<DeleteCustomerContractResponse> => {
  return del(`/customercontractv2/${id}`);
};

export interface ArchiveCustomerContractResponse {
  statusCode: number;
  message: string;
}

export const archiveCustomerContractApi = async (id: number): Promise<ArchiveCustomerContractResponse> => {
  return put(`/customercontractv2/${id}/archive`, {});
};

export const unarchiveCustomerContractApi = async (id: number): Promise<ArchiveCustomerContractResponse> => {
  return put(`/customercontractv2/${id}/unarchive`, {});
};
